import { useInsights } from '@/stores/insights/insights';
import { useJobArchitecture } from '@/stores/jobArchitecture/jobArchitecture';
import { useJobProfiles } from '@/stores/jobProfiles/jobProfiles';
import { DEMOGRAPHIC_SURVEY_SHOW } from '@/helpers/constants/demographicSurveyVersions';
import { hiringReportToOtherReport, otherReportToHiringReport } from './constants/reportingWarnings';

export const routingFromHiringReport = ({ from, to }) => {
  if (from.name === 'hiring-report' && to.name !== 'recruiting-performance') {
    useInsights().setReportingMessage(hiringReportToOtherReport);
  } else {
    useInsights().setReportingMessage({ header: '', message: '', beaconArticleId: '' });
  }
};

export const newReportsMiddleware = (authDataStore) => (to, from, next) => {
  routingFromHiringReport({ to, from });
  if (
    authDataStore.authData.isSuperuser
      || authDataStore.authData.isApplicationReportingEnabled
  ) {
    next();
  } else {
    next({ name: 'recruiting-performance' });
  }
};

export const demographicSurveyReportMiddleware = (authDataStore) => (to, from, next) => {
  routingFromHiringReport({ to, from });
  if (
    authDataStore.authData.isSuperuser
      || (authDataStore.authData.isApplicationReportingEnabled
          && authDataStore.isDemographicSurveyReportVisible)
  ) {
    next();
  } else {
    next({ name: 'recruiting-performance' });
  }
};

export const requisitionsMiddleware = (authDataStore) => (to, from, next) => {
  if (authDataStore.authData.isSuperuser
      || authDataStore.authData.isApplicationReportingEnabled) {
    next();
  } else {
    next({ name: 'root' });
  }
};

export const platformPermissionsMiddleware = (authDataStore) => (to, from, next) => {
  if (
    authDataStore.authData.isSuperuser
      || authDataStore.authData.canTeam
  ) {
    next();
  } else {
    next({ name: 'administrators' });
  }
};

export const demographicReportPermissionsMiddleware = (authDataStore) => (to, from, next) => {
  if (
    authDataStore.integrated
      && (authDataStore.authData.isSuperuser
          || (authDataStore.authData.canDemographicSurveyReport
              && authDataStore.authData.demographicSurveyReportVersion === DEMOGRAPHIC_SURVEY_SHOW))
  ) {
    next();
  } else {
    next({ name: 'administrators' });
  }
};

export const hiringReportMiddleware = (authDataStore) => (to, from, next) => {
  if (
    authDataStore.authData.isSuperuser
     || authDataStore.authData.isApplicationReportingEnabled
  ) {
    if (
      from.path.startsWith('/insights')
      && ![
        'language-snapshot',
        'usage',
        'content-snapshot',
        'weekly-snapshot',
        'compliance-report',
        'employer-brand-report',
        'job-performance-report',
      ].includes(from.name)
    ) {
      useInsights().setReportingMessage(otherReportToHiringReport);
    } else {
      useInsights().setReportingMessage({ header: '', message: '', beaconArticleId: '' });
    }

    next();
  } else {
    next({ name: 'recruiting-performance' });
  }
};

export const jobProfileMiddleware = (authDataStore) => (to, from, next) => {
  const {
    accessLevel,
    jobProfileEnabled,
    acknowledgementsEnabled,
    flsaEnabled,
  } = authDataStore.authData;

  if (
    jobProfileEnabled
    && (to.name === 'hris-job-profiles' && from.name !== 'job-architecture-compare')
  ) {
    useJobProfiles().resetSelectedProfiles();

    return next();
  }

  if (
    jobProfileEnabled
    && (to.name === 'job-architecture' && from.name !== 'job-architecture-compare')
  ) {
    useJobArchitecture().clearSelectedProfileUlids();
    useJobArchitecture().clearListFilterSnapshot();

    return next();
  }

  if (accessLevel === 3) {
    return next();
  }

  if (jobProfileEnabled) {
    if (to.name === 'employee-acknowledgements') {
      if (acknowledgementsEnabled) {
        return next();
      }

      return next({ name: 'home' });
    }

    if (to.name === 'federal-flsa-dashboard') {
      if (flsaEnabled) {
        return next();
      }

      return next({ name: 'home' });
    }

    return next();
  }

  return next({ name: 'home' });
};

export const flsaMiddleware = (authDataStore) => (to, from, next) => {
  const { jobProfileEnabled, flsaEnabled } = authDataStore.authData;

  if (jobProfileEnabled && flsaEnabled) {
    return next();
  }

  return next({ name: 'home' });
};
